import { Configuration, ProductCategoriesApi } from '@/api';
import useGetSupplierQuery from '@/api/queries/useGetSupplierQuery';
import CustomToast from '@/components/CustomToast';
import { ProductCategoryCreate, ProductCategoryUpdate } from '@/models/ProductCategory';
import { useAuth } from '@/modules/auth/contexts/AuthContext';
import ProductCategoryForm from '@/modules/product/components/ProductCategoryForm';
import { ProductCategoryListPath } from '@/modules/product/paths';
import SettingsHeader from '@/modules/settings/components/SettingsHeader';
import { FolderIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

const ProductCategoryAdd = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const { supplierId, storeId } = currentUser();

  const { supplier, isLoading } = useGetSupplierQuery(supplierId);

  const productCategoriesApi = new ProductCategoriesApi(new Configuration(useAuth));

  const handleSubmit = async (category: ProductCategoryCreate | ProductCategoryUpdate) => {
    try {
      const newCategory = await productCategoriesApi.createProductCategory(category);
      if (!newCategory) {
        toast.custom((t) => (
          <CustomToast
            id={t.id}
            message={'Failed to create product category. Please try again.'}
            visible={t.visible}
            variant="error"
          />
        ));
        return;
      }

      toast.custom((t) => (
        <CustomToast
          id={t.id}
          message={`Category "${newCategory.name}" created successfully!`}
          visible={t.visible}
        />
      ));

      navigate(ProductCategoryListPath);
    } catch (error) {
      toast.custom((t) => (
        <CustomToast
          id={t.id}
          message={'Failed to create product category. Please try again.'}
          visible={t.visible}
          variant="error"
        />
      ));
      console.error('Error while creating product category:', error);
    }
  };

  if (isLoading) {
    return;
  }

  // Show the supplier select field when the user is associated with a store (retailer)
  const showSupplierSelect = !!storeId;
  // Show the supplier text when the user is not a supplier (retailer)
  const showSupplier = !supplierId && !showSupplierSelect;

  return (
    <>
      <SettingsHeader title="Create Product Category" icon={<FolderIcon className="w-6 h-6" />} />

      <ProductCategoryForm
        onSubmit={handleSubmit}
        supplier={supplier}
        showSupplier={showSupplier}
        showSupplierSelect={showSupplierSelect}
        selectSupplierCreatedByStoreId={storeId}
      />
    </>
  );
};

export default ProductCategoryAdd;
